import React from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import keys from '../common/react-query/data-keys';
import { getProjectVersions } from '../components/project/project-version.api';
import { Card, CardHeader, Grid, LinearProgress, List, ListItem, ListItemButton, ListItemText, Stack } from '@mui/material';
import { formatDateTimeDescriptive } from '../common/utils/common.util';
import ProjectVersionWrapper from '../components/project/ProjectVersionWrapper';

const ProjectVersions: React.FC = () => {
  const { projectId } = useParams();
  const [selectedVersionId, setSelectedVersionId] = React.useState<string | null>(null);

  const {
    data: projectVersions,
    isLoading,
    isError,
  } = useQuery([keys.projectVersions, projectId], () => getProjectVersions(projectId ?? ''), {
    enabled: !!projectId,
  });

  const sortedVersions = React.useMemo(
    () => projectVersions?.sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime()),
    [projectVersions],
  );

  React.useEffect(() => {
    if (!sortedVersions) return;
    if (sortedVersions?.length > 0 && selectedVersionId == null) {
      setSelectedVersionId(sortedVersions[0].id);
    }
  }, [sortedVersions]);

  if (!projectId) {
    return (
      <Stack height={'100%'} mt={10} alignItems='center' justifyContent='center'>
        No Project Selected!
      </Stack>
    );
  }

  if (isLoading) return <LinearProgress />;
  if (isError)
    return (
      <Stack height={'100%'} mt={10} alignItems='center' justifyContent='center'>
        There was an error loading the project versions.
      </Stack>
    );

  if (!projectVersions || projectVersions.length === 0)
    return (
      <Stack height={'100%'} mt={10} alignItems='center' justifyContent='center'>
        No Project Versions Found!
      </Stack>
    );

  return (
    <Card sx={{ p: 2, height: '90vh', maxHeight: '90vh' }}>
      <CardHeader title='Project Versions' titleTypographyProps={{ variant: 'h4' }} />
      <Grid container spacing={3} sx={{ height: '90%', maxHeight: '90%', marginTop: 1, overflow: 'hidden' }}>
        <Grid size={{ xs: 3 }} sx={{ boxShadow: 3, paddingTop: '0 !important' }}>
          <List>
            {sortedVersions?.map(version => (
              <ListItem key={version.id} divider>
                <ListItemButton
                  selected={selectedVersionId == version.id}
                  onClick={() => setSelectedVersionId(version.id)}
                  sx={{ py: '16px !important' }}
                >
                  <ListItemText
                    primary={formatDateTimeDescriptive(version.createdOn)}
                    primaryTypographyProps={{
                      variant: 'body1',
                      fontWeight: 500,
                    }}
                    secondary={version.createdBy.firstName + ' ' + version.createdBy.lastName}
                    secondaryTypographyProps={{
                      variant: 'subtitle2',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Grid>
        <Grid size={{ xs: 9 }} sx={{ height: '100%', maxHeight: '100%', overflow: 'auto', paddingTop: '0 !important' }}>
          <ProjectVersionWrapper projectId={projectId} versionId={selectedVersionId} />
        </Grid>
      </Grid>
    </Card>
  );
};

export default ProjectVersions;
